import {
  DRAG_HIT_WIDTH,
  getThresholdColor,
  getThresholdSizes,
  GRIP_HEIGHT,
  GRIP_WIDTH,
  ThresholdGeometryProps,
} from "./shared";
import { pxToRadiusMm } from "portal/utils/shootTime";
import { useTheme } from "@mui/material";
import React, {
  FunctionComponent as FC,
  PointerEvent as ReactPointerEvent,
  useCallback,
} from "react";

type ThresholdHandlesProps = ThresholdGeometryProps & {
  chartWidth: number;
  maxRadiusMm: number;
  plotLeft: number;
  plotWidth: number;
  onDragStart: (index: number) => void;
  onDragMove: (index: number, radiusMm: number) => void;
  onDragEnd: (index: number) => void;
};

/** Drag hit targets + mid-plot grip handles for editable thresholds. */
export const ThresholdHandles: FC<ThresholdHandlesProps> = ({
  bands,
  chartPadding: { top: plotTop },
  chartWidth,
  displaySizes,
  maxRadiusMm,
  plotHeight,
  plotLeft,
  plotWidth,
  xScale,
  onDragStart,
  onDragMove,
  onDragEnd,
}) => {
  const {
    palette: {
      background: { paper: gripFill },
    },
  } = useTheme();
  const sizes = getThresholdSizes(bands, displaySizes);

  const onThresholdPointerDown = useCallback(
    (index: number, event: ReactPointerEvent<SVGElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const target = event.currentTarget;
      const svg = target.ownerSVGElement;
      if (!svg) {
        return;
      }
      // Keeps getting move/up even if the cursor leaves the grip.
      target.setPointerCapture(event.pointerId);
      onDragStart(index);

      const move = (ev: PointerEvent): void => {
        if (ev.pointerId !== event.pointerId) {
          return;
        }
        const rect = svg.getBoundingClientRect();
        if (rect.width <= 0) {
          return;
        }
        const xSvg = ((ev.clientX - rect.left) / rect.width) * chartWidth;
        const mm = pxToRadiusMm(xSvg, plotLeft, plotWidth, maxRadiusMm);
        onDragMove(index, mm);
      };
      const stop = (ev: PointerEvent): void => {
        if (ev.pointerId !== event.pointerId) {
          return;
        }
        // using window and not target intentionally - so that we don't miss pointerups that happen off element with vigorous drags
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", stop);
        window.removeEventListener("pointercancel", stop);
        target.removeEventListener("lostpointercapture", stop);
        if (target.hasPointerCapture(ev.pointerId)) {
          target.releasePointerCapture(ev.pointerId);
        }
        onDragEnd(index);
      };
      // Window listeners survive SVG remounts / lost capture on the grip node.
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", stop);
      window.addEventListener("pointercancel", stop);
      target.addEventListener("lostpointercapture", stop);
    },
    [
      chartWidth,
      maxRadiusMm,
      onDragEnd,
      onDragMove,
      onDragStart,
      plotLeft,
      plotWidth,
    ]
  );

  return sizes.map((threshold, index) => {
    const color = getThresholdColor(bands, index);
    const x = xScale(threshold);
    const gripY = plotTop + plotHeight / 2;

    return (
      <g key={bands[index]?.label ?? threshold}>
        <rect
          x={x - DRAG_HIT_WIDTH / 2}
          y={plotTop}
          width={DRAG_HIT_WIDTH}
          height={plotHeight}
          fill="transparent"
          className="cursor-ew-resize"
          onPointerDown={(event) => {
            onThresholdPointerDown(index, event);
          }}
        />
        <g
          transform={`translate(${x}, ${gripY})`}
          className="cursor-ew-resize"
          onPointerDown={(event) => {
            onThresholdPointerDown(index, event);
          }}
        >
          <rect
            x={-GRIP_WIDTH / 2}
            y={-GRIP_HEIGHT / 2}
            width={GRIP_WIDTH}
            height={GRIP_HEIGHT}
            rx={6}
            fill={gripFill}
            stroke={color}
            strokeWidth={1.5}
          />
          {[-5, 0, 5].map((lineY) => (
            <line
              key={lineY}
              x1={-5}
              x2={5}
              y1={lineY}
              y2={lineY}
              stroke={color}
              strokeWidth={1.5}
              strokeLinecap="round"
              pointerEvents="none"
            />
          ))}
        </g>
      </g>
    );
  });
};
